import { useMemo, useState } from 'react';
import { Search as SearchIcon } from '@/components/icons';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/form/Select';
import { Input } from '@/components/form/Input';
import { listSisLedger, listSisStores, type SisLedgerRow } from '@/api/sisStock';
import { formatDate, formatINRSymbol } from '@/lib/format';

const TXN_TONE: Record<string, 'success' | 'danger' | 'muted'> = {
  Dispatch: 'success',
  Sales: 'danger',
  Return: 'success',
  Reversal: 'muted',
  Adjustment: 'muted',
};

export function SisLedgerPage() {
  const [params, setParams] = useSearchParams();
  const initialStore = params.get('partyId') ?? '';
  const initialItem = params.get('itemId') ?? '';
  const [partyId, setPartyId] = useState(initialStore);
  const [itemId, setItemId] = useState(initialItem);
  const [txnType, setTxnType] = useState('');
  const [search, setSearch] = useState('');

  const filter = useMemo(
    () => ({
      partyId: partyId ? Number(partyId) : undefined,
      itemId: itemId ? Number(itemId) : undefined,
      txnType: txnType || undefined,
    }),
    [partyId, itemId, txnType],
  );

  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });
  const storesByGroup = useMemo(() => {
    const m = new Map<string, { id: number; name: string; city: string }[]>();
    for (const s of storesQ.data ?? []) {
      const g = s.group || 'Ungrouped';
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(s);
    }
    return Array.from(m.entries()).sort(([a], [b]) =>
      a === 'Ungrouped' ? 1 : b === 'Ungrouped' ? -1 : a.localeCompare(b),
    );
  }, [storesQ.data]);
  const query = useQuery({
    queryKey: ['sis-ledger', filter],
    queryFn: () => listSisLedger(filter),
    refetchInterval: 60_000,
  });
  const rows = query.data ?? [];

  function clearItem() {
    setItemId('');
    if (params.get('itemId')) {
      params.delete('itemId');
      setParams(params, { replace: true });
    }
  }

  return (
    <div>
      <SectionHeader title="SIS Ledger" />

      <FilterBar>
        <FilterField label="Search" className="sm:col-auto">
          <div className="relative w-full">
            <SearchIcon
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[240px]"
              aria-label="Search SIS ledger"
            />
          </div>
        </FilterField>
        <FilterField label="Store">
          <Select value={partyId} onChange={(e) => setPartyId(e.target.value)}>
            <option value="">All Stores</option>
            {storesByGroup.map(([g, list]) => (
              <optgroup key={g} label={g}>
                {list.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.city ? ` (${s.city})` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </FilterField>
        <FilterField label="Txn Type">
          <Select value={txnType} onChange={(e) => setTxnType(e.target.value)}>
            <option value="">All</option>
            <option>Dispatch</option>
            <option>Sales</option>
            <option>Return</option>
            <option>Reversal</option>
            <option>Adjustment</option>
          </Select>
        </FilterField>
        {itemId && (
          <button type="button" onClick={clearItem} className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs sm:col-span-1 sm:ml-auto sm:self-end">
            Clear item filter ({itemId})
          </button>
        )}
      </FilterBar>

      <Table<SisLedgerRow>
        storageKey="sis-ledger"
        paginated
        title="SIS Movements"
        count={`${rows.length} txns`}
        rows={rows}
        search={search}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        columns={[
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.txnDate) },
          {
            key: 'type',
            header: 'Type',
            cell: (r) => <Badge tone={TXN_TONE[r.txnType] ?? 'muted'}>{r.txnType}</Badge>,
          },
          { key: 'ref', header: 'Ref', cell: (r) => r.refDoc ?? '—' },
          { key: 'store', header: 'Store', cell: (r) => <span className="font-bold">{r.storeName}</span> },
          { key: 'code', header: 'Item', cell: (r) => <span><span className="font-extrabold text-accent">{r.itemCode}</span> · {r.itemName}</span> },
          {
            key: 'in',
            header: 'In',
            align: 'right',
            cell: (r) => (r.qtyIn > 0 ? <span className="text-emerald-600">+{r.qtyIn}</span> : '—'),
          },
          {
            key: 'out',
            header: 'Out',
            align: 'right',
            cell: (r) => (r.qtyOut > 0 ? <span className="text-rose-600">-{r.qtyOut}</span> : '—'),
          },
          {
            key: 'rate',
            header: 'Rate',
            align: 'right',
            cell: (r) => (r.rate ? formatINRSymbol(r.rate) : '—'),
          },
          {
            key: 'value',
            header: 'Value',
            align: 'right',
            cell: (r) =>
              r.value ? (
                <span className={r.qtyIn > 0 ? 'text-emerald-600' : 'text-rose-600'}>{formatINRSymbol(r.value)}</span>
              ) : (
                '—'
              ),
          },
          {
            key: 'bal',
            header: 'Balance',
            align: 'right',
            cell: (r) => <strong>{r.balance}</strong>,
          },
          { key: 'remarks', header: 'Remarks', cell: (r) => r.remarks ?? '—' },
        ]}
      />
    </div>
  );
}
